import React from "react"
import { Button } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import { usePartner } from "@src/partner"
import {
  ReportedStolenFilter,
  LockStateFilter,
  CriticalBatteryFilter,
  ServiceModeFilter,
} from "../types"
import { FilterToggleButton } from "./FilterToggleButton"

type FilterBarProps = {
  filterReportedStolen: ReportedStolenFilter
  setFilterReportedStolen: (filter: ReportedStolenFilter) => void
  filterLockState: LockStateFilter
  setFilterLockState: (filter: LockStateFilter) => void
  filterCriticalBattery: CriticalBatteryFilter
  setFilterCriticalBattery: (filter: CriticalBatteryFilter) => void
  filterServiceMode: ServiceModeFilter
  setFilterServiceMode: (filter: ServiceModeFilter) => void
  hasActiveFilters: boolean
  onResetFilters: () => void
  onUpdateParam: (key: string, value?: string | null) => void
  searchParams: URLSearchParams
  setSearchParams: (params: URLSearchParams) => void
  setActivePage: (page: number) => void
}

const activeToggleStyle = (active: boolean, brandColor?: string): React.CSSProperties =>
  active && brandColor
    ? { backgroundColor: brandColor, borderColor: brandColor, color: "#fff" }
    : {}

export const FilterBar: React.FC<FilterBarProps> = ({
  filterReportedStolen,
  setFilterReportedStolen,
  filterLockState,
  setFilterLockState,
  filterCriticalBattery,
  setFilterCriticalBattery,
  filterServiceMode,
  setFilterServiceMode,
  hasActiveFilters,
  onResetFilters,
  onUpdateParam,
  searchParams,
  setSearchParams,
  setActivePage,
}) => {
  const { t } = useTranslation()
  const { brand } = usePartner()

  const handleServiceModeChange = (next: ServiceModeFilter) => {
    setFilterServiceMode(next)
    const currentParams = new URLSearchParams(searchParams)

    if (next === "active") {
      currentParams.set("servicebooking_status", "active")
      currentParams.delete("servicebooking_end_before")
    } else if (next === "expires_90d") {
      const target = new Date()
      target.setDate(target.getDate() + 90)
      const yyyy = target.getFullYear()
      const mm = String(target.getMonth() + 1).padStart(2, "0")
      const dd = String(target.getDate()).padStart(2, "0")
      currentParams.set("servicebooking_end_before", `${yyyy}-${mm}-${dd}`)
      currentParams.delete("servicebooking_status")
    } else if (next === "no_service") {
      currentParams.set("servicebooking_status", "none")
      currentParams.delete("servicebooking_end_before")
    } else {
      currentParams.delete("servicebooking_status")
      currentParams.delete("servicebooking_end_before")
    }

    setActivePage(0)
    setSearchParams(currentParams)
  }

  return (
    <div className="d-flex align-items-end flex-wrap gap-2 mb-3" style={{ rowGap: 8 }}>
      {/* Reported Stolen Filter */}
      <div className="d-flex flex-column">
        <div
          className="btn-group btn-group-sm"
          role="group"
          aria-label={t("devices_screen.filter.theft_group") || "Theft reported filter"}
        >
          <FilterToggleButton
            id="filter-theft-yes"
            active={filterReportedStolen === "true"}
            onToggle={() => {
              const next: ReportedStolenFilter = filterReportedStolen === "true" ? "any" : "true"
              setFilterReportedStolen(next)
              onUpdateParam("reported_stolen", next === "any" ? undefined : "true")
            }}
            label={t("devices_screen.filter.theft_open")}
            style={activeToggleStyle(filterReportedStolen === "true", brand?.color)}
          />
          <FilterToggleButton
            id="filter-theft-no"
            active={filterReportedStolen === "false"}
            onToggle={() => {
              const next: ReportedStolenFilter = filterReportedStolen === "false" ? "any" : "false"
              setFilterReportedStolen(next)
              onUpdateParam("reported_stolen", next === "any" ? undefined : "false")
            }}
            label={t("devices_screen.filter.theft_none")}
            style={activeToggleStyle(filterReportedStolen === "false", brand?.color)}
          />
        </div>
      </div>

      {/* Lock State Filter */}
      <div className="d-flex flex-column">
        <div
          className="btn-group btn-group-sm"
          role="group"
          aria-label={t("devices_screen.filter.lock_group") || "Lock state filter"}
        >
          <FilterToggleButton
            id="filter-lock-locked"
            active={filterLockState === "locked"}
            onToggle={() => {
              const next: LockStateFilter = filterLockState === "locked" ? "any" : "locked"
              setFilterLockState(next)
              onUpdateParam("lock_state", next === "any" ? undefined : "locked")
            }}
            label={t("devices_screen.filter.lock_locked")}
            style={activeToggleStyle(filterLockState === "locked", brand?.color)}
          />
          <FilterToggleButton
            id="filter-lock-open"
            active={filterLockState === "open"}
            onToggle={() => {
              const next: LockStateFilter = filterLockState === "open" ? "any" : "open"
              setFilterLockState(next)
              onUpdateParam("lock_state", next === "any" ? undefined : "open")
            }}
            label={t("devices_screen.filter.lock_open")}
            style={activeToggleStyle(filterLockState === "open", brand?.color)}
          />
        </div>
      </div>

      {/* Critical Battery Filter */}
      <div className="d-flex flex-column" style={{ maxWidth: 220 }}>
        <FilterToggleButton
          id="filter-critical-battery"
          active={filterCriticalBattery === "true"}
          onToggle={() => {
            const next: CriticalBatteryFilter = filterCriticalBattery === "true" ? "any" : "true"
            setFilterCriticalBattery(next)
            onUpdateParam("critical_battery", next === "true" ? "true" : undefined)
          }}
          label={t("devices_screen.filter.battery_critical")}
          small
          style={activeToggleStyle(filterCriticalBattery === "true", brand?.color)}
        />
      </div>

      {/* Service Mode Filter */}
      <div className="d-flex flex-column">
        <div
          className="btn-group btn-group-sm"
          role="group"
          aria-label={t("devices_screen.filter.service_group") || "Service status filter"}
        >
          <FilterToggleButton
            id="filter-service-active"
            active={filterServiceMode === "active"}
            onToggle={() =>
              handleServiceModeChange(filterServiceMode === "active" ? "any" : "active")
            }
            label={t("devices_screen.filter.service_active")}
            style={activeToggleStyle(filterServiceMode === "active", brand?.color)}
          />
          <FilterToggleButton
            id="filter-service-exp90"
            active={filterServiceMode === "expires_90d"}
            onToggle={() =>
              handleServiceModeChange(filterServiceMode === "expires_90d" ? "any" : "expires_90d")
            }
            label={t("devices_screen.filter.service_exp_90d")}
            style={activeToggleStyle(filterServiceMode === "expires_90d", brand?.color)}
          />
          <FilterToggleButton
            id="filter-service-none"
            active={filterServiceMode === "no_service"}
            onToggle={() =>
              handleServiceModeChange(filterServiceMode === "no_service" ? "any" : "no_service")
            }
            label={t("devices_screen.filter.service_none")}
            style={activeToggleStyle(filterServiceMode === "no_service", brand?.color)}
          />
        </div>
      </div>

      {/* Reset Filters Button */}
      {hasActiveFilters && (
        <div className="d-flex flex-column">
          <Button size="sm" variant="primary" onClick={onResetFilters}>
            {t("devices_screen.filter.reset")}
          </Button>
        </div>
      )}
    </div>
  )
}
